import React, { useState } from 'react';
import {
  useStripe,
  ExpressCheckoutElement,
  useElements,
} from '@stripe/react-stripe-js';

import { useIntl } from 'react-intl';

import {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent,
} from '@stripe/stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import {
  setBillingAddress,
  setShippingAddressSteppedCheckout,
  getTaxEstimation,
} from '../../slices/cart';
import { paymentMethods } from '../../slices/payments';
import { NON_CONTINENTAL_STATES } from '../../blocks/pdp/constants';
import { CartInStore } from '../../slices/cart/types';
import store from '../../store';
import styles from './ExpressPayments.module.scss';

import {
  GetShippingMethodsResponse,
  SetShippingMethodsEntry,
} from '../../slices/shippingMethods/types';
import {
  getAvailableShippingMethods,
  setShippingMethodsForAllProductsInCart,
} from '../../slices/shippingMethods';
import usePaymentMethodsSelector from '../../hooks/usePaymentMethodsSelector';
import { waitForUtag } from '../../utils/tealium';
import { IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY } from '../../utils/common/constants';
import cartService from '../../services/cart';

const ExpressPayments = ({
  onHandleSavePaymentMethodData,
}: {
  onHandleSavePaymentMethodData: () => void;
}) => {
  const { locale } = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const { changeSelectedPaymentMethod } = usePaymentMethodsSelector();

  const { taxedPrice, shippingAddress: cartShippingAddress } = useSelector(
    (state: { cart: CartInStore }) => state.cart,
  );

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    // eslint-disable-next-line no-console
    console.log('Stripe Express - onConfirm called with event:', event);
    if (!stripe) {
      return;
    }

    if (!elements) {
      // eslint-disable-next-line no-console
      console.error('Stripe Elements not loaded');
      return;
    }

    const { shippingAddress, billingDetails, paymentFailed } = event;

    const selectedShippingAddress = shippingAddress?.address;

    if (NON_CONTINENTAL_STATES.includes(selectedShippingAddress?.state ?? '')) {
      paymentFailed({ reason: 'invalid_shipping_address' });
    }
    changeSelectedPaymentMethod(paymentMethods.CREDIT_CARD);

    const [shippingFirstName = '', ...rest] = (shippingAddress?.name ?? '')
      .trim()
      .split(' ');
    const shippingLastName = rest.length ? rest.join(' ') : '';

    const shippingAddressDraftToAssign = {
      streetAddress: shippingAddress?.address?.line1 ?? '',
      city: selectedShippingAddress?.city ?? '',
      postalCode: selectedShippingAddress?.postal_code ?? '',
      country: selectedShippingAddress?.country ?? '',
      state: selectedShippingAddress?.state ?? '',
      additionalStreetInfo: shippingAddress?.address?.line2 ?? '',
      email: billingDetails?.email ?? '',
      phone: billingDetails?.phone ?? '',
      firstName: shippingFirstName,
      lastName: shippingLastName,
    };

    // eslint-disable-next-line @typescript-eslint/await-thenable
    await dispatch(
      setShippingAddressSteppedCheckout({
        shippingAddress: shippingAddressDraftToAssign,
      }),
    );

    localStorage.setItem(
      'expressShippingAddress',
      JSON.stringify(shippingAddressDraftToAssign),
    );

    // eslint-disable-next-line @typescript-eslint/await-thenable
    await dispatch(
      setBillingAddress({
        billingAddress: shippingAddressDraftToAssign,
      }),
    );

    // eslint-disable-next-line @typescript-eslint/await-thenable
    await dispatch(
      getAvailableShippingMethods({
        locale,
        overwritePaidInHomeSetupShipping: false,
        customInHomeSetupPricing: false,
      }),
    );

    const { shippingMethods } = store.getState() as {
      cart: CartInStore;
      shippingMethods: {
        availableShippingMethods: GetShippingMethodsResponse;
      };
    };

    const { availableShippingMethods } = shippingMethods;
    // eslint-disable-next-line no-console
    console.log('Stripe - availableShippingMethods', availableShippingMethods);

    const shippingMethodsToAssign: SetShippingMethodsEntry[] =
      availableShippingMethods.map((shippingMethod) => ({
        parentKey: shippingMethod.key,
        groups: shippingMethod.groups.map((group) => {
          const isInHomeSetupFreeEligible = !!group.shippingMethods.find(
            (method) => method.key === IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
          );

          if (isInHomeSetupFreeEligible) {
            return {
              key: IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
              name: 'In-Home Setup',
              cost: 0,
              needsRemoval: true,
              skus: group.products.map((product) => product.sku),
            };
          }

          return {
            key: 'FEDEX_GROUND',
            name: 'Standard Shipping',
            cost: 0,
            needsRemoval: false,
            skus: group.products.map((product) => product.sku),
          };
        }),
      }));

    // eslint-disable-next-line @typescript-eslint/await-thenable
    dispatch(
      setShippingMethodsForAllProductsInCart({
        shippingMethods: shippingMethodsToAssign,
        overwritePaidInHomeSetupShipping: false,
        customInHomeSetupPricing: false,
      }),
    );

    // eslint-disable-next-line no-console
    console.log(
      'Stripe  shippingAddressDraftToAssign',
      shippingAddressDraftToAssign,
    );

    onHandleSavePaymentMethodData();
  };

  const showPaypalButton = window.purTogglesEnabled('paypal_buttons') ?? false;

  const options: StripeExpressCheckoutElementOptions = {
    allowedShippingCountries: ['US'],
    emailRequired: true,
    shippingAddressRequired: true,
    billingAddressRequired: true,
    phoneNumberRequired: true,
    buttonHeight: 40,
    layout: {
      maxColumns: 2,
      overflow: 'never',
    },
    paymentMethods: {
      paypal: showPaypalButton ? 'auto' : 'never',
      klarna: 'never',
    },
    paymentMethodOrder: ['apple_pay', 'amazon_pay', 'link', 'klarna'],
  };

  // We need the untaxed amount to calculate estimated totals for Stripe's
  // onShippingAddressChange callback.
  const totalUntaxed = useSelector(
    (state: {
      cart: {
        taxedPrice: {
          totalGross: { centAmount: number; fractionDigits: number };
        };
        estimatedTaxAmount: { centAmount: number };
        totalPrice: { centAmount: number; fractionDigits: number };
      };
    }) => ({
      ...state.cart?.totalPrice,
    }),
  );

  const onShippingAddressChange = async (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent,
  ) => {
    // eslint-disable-next-line no-console
    console.log('Stripe Express - Shipping address changed:', event);

    const { resolve, reject, address } = event;
    // eslint-disable-next-line no-console
    console.log('Stripe Express - Shipping address changed - address', address);
    const selectedShippingAddress = address;

    if (NON_CONTINENTAL_STATES.includes(selectedShippingAddress.state ?? '')) {
      reject();
    } else {
      const payload = {
        shippingRates: [
          {
            id: crypto.randomUUID(),
            amount: 0,
            displayName: 'Standard Shipping',
          },
        ],
      };

      const zipCode = address.postal_code;

      // eslint-disable-next-line no-console
      console.log('Stripe Express - zipCode', zipCode);

      if (zipCode) {
        // eslint-disable-next-line @typescript-eslint/await-thenable
        await dispatch(getTaxEstimation({ zipCode }));

        // State is not being updated fast enough, so the Stripe context does
        // not reflect the most recently calculated amount. We need to call the
        // tax service directly for now and add it to the cart total.
        interface TaxEstimation {
          centAmount: number;
          currencyCode: string;
          fractionDigits: number;
        }

        let taxAmount;
        try {
          taxAmount = (await cartService.getTaxEstimation(
            zipCode,
          )) as TaxEstimation;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching tax estimation:', error);
        }

        const sameZipcodeAsCart = cartShippingAddress?.postalCode === zipCode;

        const newTotal =
          (!cartShippingAddress ||
            (cartShippingAddress && !sameZipcodeAsCart)) &&
          taxAmount?.centAmount
            ? totalUntaxed.centAmount + taxAmount.centAmount
            : taxedPrice?.totalGross.centAmount;

        if (elements) {
          elements.on('update-end', () => {
            resolve(payload);
          });
          elements.update({
            mode: 'payment',
            currency: 'usd',
            amount: newTotal,
          });
        }
      }
    }
  };

  const onShippingRateChange = (
    event: StripeExpressCheckoutElementShippingRateChangeEvent,
  ) => {
    // TODO Stripe: Implement onShippingRateChange
    // This function can be used to handle changes in the shipping rate
    // eslint-disable-next-line no-console
    console.log('Stripe Express - Shipping rate changed:', event);
    const { resolve, reject } = event;
    try {
      resolve();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error resolving shipping rate change:', error);
      reject();
    }
  };

  const onCancel = (event: unknown) => {
    // TODO Stripe: Implement onCancel
    // This function can be used to handle cancellation of the payment
    // eslint-disable-next-line no-console
    console.log('Stripe Express - Payment cancelled:', event);
  };

  const onClick = (event: StripeExpressCheckoutElementClickEvent) => {
    // TODO Stripe: Implement onClick
    // This function will trigger a utag click event
    // eslint-disable-next-line no-console
    const { expressPaymentType, resolve } = event;
    waitForUtag(() => {
      window.utag.link({
        event_name: `${expressPaymentType} Button Clicked`,
        page_name: 'checkout',
      });
    });
    resolve();
  };

  const [isStripeLoading, setIsStripeLoading] = useState(true);

  return (
    <div id="stripe-express-checkout">
      {isStripeLoading && (
        <div className={styles['stripe-element-loading']}>
          <div className={styles['stripe-element-loading-button']} />
          <div className={styles['stripe-element-loading-button']} />
          <div className={styles['stripe-element-loading-button']} />
        </div>
      )}
      <ExpressCheckoutElement
        onConfirm={onConfirm}
        options={options}
        onShippingAddressChange={onShippingAddressChange}
        onShippingRateChange={onShippingRateChange}
        onCancel={onCancel}
        onClick={onClick}
        onReady={() => setIsStripeLoading(false)}
      />
    </div>
  );
};

export default ExpressPayments;
